import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { notificationApi } from '../api/notificationApi';
import { connectSocket, getSocket } from '../socket/socketClient';

const timeAgo = (date) => {
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const NotificationBell = ({ actionReminder = null }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const actionCount = Number(actionReminder?.count) || 0;
  const totalCount = count + actionCount;
  const syntheticReminder = useMemo(() => actionCount > 0 ? {
    _id: '__provider-action-reminder',
    read: false,
    title: actionReminder?.title || 'Action required in Settings',
    body: actionReminder?.body || `${actionCount} verification item${actionCount === 1 ? '' : 's'} still need your attention.`,
    link: actionReminder?.link || '/provider/settings',
    data: { tab: actionReminder?.tab || 'documents' },
    createdAt: new Date().toISOString(),
    synthetic: true,
  } : null, [actionCount, actionReminder]);

  useEffect(() => { notificationApi.unreadCount().then((response) => setCount(response.data?.count || 0)).catch(() => {}); }, []);

  useEffect(() => {
    const socket = getSocket() || connectSocket();
    if (!socket) return undefined;
    const onNew = (notification) => { setCount((current) => current + 1); setItems((list) => [notification, ...list].slice(0, 30)); };
    socket.on('notification:new', onNew);
    return () => socket.off('notification:new', onNew);
  }, []);

  useEffect(() => {
    const onDocument = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocument); document.removeEventListener('keydown', onKey); };
  }, []);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setLoading(true);
    try { const response = await notificationApi.list(); setItems(response.data?.data || []); }
    catch { /* Keep existing list when refresh fails. */ }
    finally { setLoading(false); }
  }, [open]);

  const openItem = (notification) => {
    if (!notification.synthetic && !notification.read) {
      notificationApi.markRead(notification._id).catch(() => {});
      setCount((current) => Math.max(0, current - 1));
      setItems((list) => list.map((item) => item._id === notification._id ? { ...item, read: true } : item));
    }
    setOpen(false);
    if (notification.link) navigate(notification.link, notification.data?.tab ? { state: { tab: notification.data.tab } } : undefined);
  };

  const markAll = () => {
    notificationApi.markAllRead().catch(() => {});
    setCount(0);
    setItems((list) => list.map((item) => ({ ...item, read: true })));
  };

  const visibleItems = syntheticReminder ? [syntheticReminder, ...items.filter((item) => item.type !== 'verification_action_required')] : items;

  return (
    <div className="relative" ref={ref}>
      <button type="button" className="relative flex h-10 w-10   items-center justify-center rounded-[10px] border border-slate-200 bg-white text-giggs-text transition-all duration-200 hover:border-giggs-green hover:text-giggs-green hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-giggs-green/10" onClick={toggle} aria-label="Notifications" aria-expanded={open}>
        <Bell size={20} />
        {totalCount > 0 && <span className="absolute -right-1 -top-1 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 font-dm text-[10px] font-semibold leading-none text-white">{totalCount > 9 ? '9+' : totalCount}</span>}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[68px] z-[1200] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_55px_rgba(16,39,29,0.18)] sm:absolute sm:left-auto sm:right-0 sm:top-[calc(100%+10px)] sm:w-[360px]" role="menu">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3.5"><span className="font-nunito text-sm font-bold text-giggs-ink">Notifications</span>{count > 0 && <button type="button" className="inline-flex   items-center gap-1 border-0 bg-transparent p-0 font-dm text-xs font-medium text-giggs-green hover:text-giggs-green-hover" onClick={markAll}><Check size={13} /> Mark all read</button>}</div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? <div className="px-4 py-10 text-center font-dm text-sm text-giggs-muted">Loading…</div> : visibleItems.length === 0 ? <div className="px-4 py-10 text-center font-dm text-sm text-giggs-muted">You're all caught up.</div> : visibleItems.map((notification) => (
              <button type="button" key={notification._id} className={`flex w-full   items-start gap-3 border-0 border-b border-slate-100 px-4 py-3.5 text-left transition last:border-b-0 hover:bg-slate-50 ${notification.read ? 'bg-white' : 'bg-giggs-green/[0.045]'}`} onClick={() => openItem(notification)}>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.read ? 'bg-slate-200' : notification.synthetic ? 'bg-amber-500' : 'bg-giggs-green'}`} />
                <span className="min-w-0 flex-1"><span className="block font-dm text-[13px] font-semibold text-giggs-ink">{notification.title}</span>{notification.body && <span className="mt-0.5 block break-words font-dm text-xs font-normal leading-5 text-giggs-text">{notification.body}</span>}<span className="mt-1 block font-dm text-[11px] font-normal text-giggs-muted">{notification.synthetic ? 'Needs action' : timeAgo(notification.createdAt)}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
